import Utils from './Utils';

class RSA_OAEP  {

    static decodeKey (pem) {

        let lines = pem.split('\n');
        let encoded = '';

        for ( var i = 0; i < lines.length; i++ ) {

            if ( lines[i].trim().length > 0 && lines[i].indexOf('-BEGIN ') < 0 && lines[i].indexOf('-END ') <  0) {
                encoded += lines[i].trim();
            }
        }

        let byteStr = atob(encoded);
        let bytes = new Uint8Array(byteStr.length);

        for ( var i = 0; i < byteStr.length; i++ ) {
            bytes[i] = byteStr.charCodeAt(i);
        }

        return bytes;
    }

    static genkeys (bitsize) {

        return new Promise( (resolve, reject) => {

            bitsize = ( bitsize === undefined ) ? 2048 : bitsize;

            if ( window.crypto.subtle ) {

                window.crypto.subtle.generateKey(
                    {
                        name: "RSA-OAEP",
                        modulusLength: bitsize,
                        publicExponent: new Uint8Array([1, 0, 1]),
                        hash: {
                            name: "SHA-1"
                        },
                    },
                    true,
                    ["encrypt", "decrypt"]
                ).then( (key) => {

                    resolve({
                        'public': key.publicKey,
                        'private': key.privateKey
                    });

                }).catch(reject);

            } else {
                console.warn('@nutshell/core: Encryption is only allowed on HTTPS');
                reject();
            }
        });
    }

    static importPrivateKey(key) {

        return new Promise( (resolve, reject) => {

            if ( key ) {

                if ( window.crypto.subtle ) {

                    window.crypto.subtle.importKey(
                        "jwk",
                        key,
                        {
                            name: "RSA-OAEP",
                            hash: {name: "SHA-256"}, //can be "SHA-1", "SHA-256", "SHA-384", or "SHA-512"
                        },
                        true,
                        ["decrypt"]
                    ).then(resolve).catch(reject);
                }
                else {
                    console.warn('@nutshell/core: Encryption is only allowed on HTTPS');
                    reject();
                }
            }
            else { reject(); }
        });
    }

    static importPublicKey(key) {

        return new Promise( (resolve, reject) => {

            if ( key ) {

                window.crypto.subtle.importKey(
                    "jwk",
                    key,
                    {
                        name: "RSA-OAEP",
                        hash: {name: "SHA-256"}, //can be "SHA-1", "SHA-256", "SHA-384", or "SHA-512"
                    },
                    true,
                    ["encrypt"]
                ).then(resolve).catch(reject);
            }
            else { reject(); }
        });
    }

    static importPublicKeyPEM (pem) {

        return new Promise( (resolve, reject) => {

            if ( typeof pem === "string" && pem.match(/^-----BEGIN PUBLIC KEY-----/) ) {

                let decoded = RSA_OAEP.decodeKey(pem);

                if ( decoded ) {

                    window.crypto.subtle.importKey(
                        "spki",
                        decoded,
                        {
                            'name': 'RSA-OAEP',
                            'hash': {'name': 'SHA-1'}
                        },
                        true,
                        ["encrypt"]
                    ).then(resolve).catch(reject);
                }
            }
            else { reject(); }

        });
    }

    static importPrivateKeyPEM(pem) {

        return new Promise( (resolve, reject) => {

            if ( typeof pem === "string" && pem.match(/^-----BEGIN PRIVATE KEY-----/) ) {

                let decoded = RSA_OAEP.decodeKey(pem);

                if ( decoded ) {

                    window.crypto.subtle.importKey(
                        "pkcs8",
                        decoded,
                        {
                            'name': 'RSA-OAEP',
                            'hash': {'name': 'SHA-1'},
                            // modulusLength: 2048,
                            publicExponent: new Uint8Array([1, 0, 1])
                        },
                        true,
                        ["decrypt"]
                    ).then(resolve).catch(reject);

                }
                else { reject(); }
            }
            else { reject(); }
        });
    }

    static exportKey(key) {

        return new Promise( (resolve, reject) => {

            if ( key ) {

                window.crypto.subtle.exportKey(
                    "jwk",
                    key
                ).then(resolve).catch(reject);

            }
            else { reject(); }
        });
    }

    static exportPublicKeyPEM(key) {

        return new Promise( (resolve, reject) => {

            if ( key ) {

                window.crypto.subtle.exportKey(
                    "spki",
                    key
                ).then( ( exportedKey ) => {

                    if ( exportedKey !== undefined ) {
                        const exportedAsString = Utils.arrayBufferToString(exportedKey);
                        const exportedAsBase64 = window.btoa(exportedAsString);
                        const pem = `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`;

                        resolve(pem);
                    }
                    else { reject(); }

                }).catch(reject);
            }
            else { reject(); }
        });
    }

    static exportPrivateKeyPEM(key) {

        return new Promise( (resolve, reject) => {

            if ( key ) {

                window.crypto.subtle.exportKey(
                    "pkcs8",
                    key
                ).then( (exportedKey) => {

                    if ( exportedKey !== undefined ) {

                        const exportedAsString = Utils.arrayBufferToString(exportedKey);
                        const exportedAsBase64 = window.btoa(exportedAsString);
                        const pem = `-----BEGIN PRIVATE KEY-----\n${exportedAsBase64}\n-----END PRIVATE KEY-----`;

                        resolve(pem);
                    }
                    else { reject(); }

                }).catch(reject);
            }
            else { reject(); }

        });
    }

    static encrypt(data, publicKey) {

        return new Promise( (resolve, reject) => {

            if ( data && publicKey ) {

                window.crypto.subtle.encrypt(
                    {
                        'name': 'RSA-OAEP',
                        'hash': {'name': 'SHA-256'},
                    },
                    publicKey,
                    ( data instanceof Uint8Array ) ? data : Utils.stringToBytes(data)
                ).then( (encrypted) => {

                    if ( encrypted ) {
                        resolve(new Uint8Array(encrypted));
                    }
                    else { reject(); }

                }).catch(reject);
            }
            else { reject(); }

        });
    }

    static decrypt(encrypted, privateKey) {

        return new Promise( (resolve, reject) => {

            if ( encrypted && privateKey ) {

                window.crypto.subtle.decrypt(
                    {
                        'name': 'RSA-OAEP',
                    },
                    privateKey,
                    ( encrypted instanceof Uint8Array ) ? encrypted : Utils.stringToBytes(encrypted)
                ).then( (decrypted) => {

                    if ( decrypted ) {
                        resolve(new Uint8Array(decrypted));
                    }
                    else { reject(); }

                }).catch(reject);
            }
        });
    }
}

export default RSA_OAEP;
